import serviciosV2Client from "../../../helpers/serviciosV2Client"

const ART = "articulos"
const EXPORT_BASE = "export/articulos/global"

const cleanParams = obj =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== "" && v !== null && v !== undefined),
  )

const baseFiltros = ({ nitemisor, fecha_inicio, fecha_fin, campo_fecha, sucursal }) =>
  cleanParams({ nitemisor, fecha_inicio, fecha_fin, campo_fecha, sucursal })

/** Lista paginada con TODAS las dimensiones (cantidades, financiero, precios, etc.) */
export const getArticulosGlobal = ({ order_by, order_dir, limit, offset, ...filtros }) =>
  serviciosV2Client.get(`${ART}/global`, {
    params: cleanParams({
      ...baseFiltros(filtros),
      order_by, order_dir, limit, offset,
    }),
  })

/** KPIs ejecutivos + matriz BCG + concentración + insights globales */
export const getArticulosResumen = (filtros) =>
  serviciosV2Client.get(`${ART}/global/resumen`, { params: baseFiltros(filtros) })

/** Pareto ABC graficable */
export const getArticulosPareto = (filtros) =>
  serviciosV2Client.get(`${ART}/global/abc-pareto`, { params: baseFiltros(filtros) })

/** Comparar 2-5 productos lado a lado */
export const compararArticulos = ({ ids, ...filtros }) =>
  serviciosV2Client.get(`${ART}/comparar`, {
    params: cleanParams({ ...baseFiltros(filtros), ids: Array.isArray(ids) ? ids.join(",") : ids }),
  })

/** Detalle profundo de UN producto: serie diaria, comparativo, top clientes, co-purchase, etc. */
export const getArticuloDetalle = (idProducto, filtros) =>
  serviciosV2Client.get(`${ART}/${encodeURIComponent(idProducto)}/detalle`, {
    params: baseFiltros(filtros),
  })

/** Listado paginado de facturas que contienen un producto específico. */
export const getArticuloFacturas = (idProducto, { limit = 100, offset = 0, ...filtros }) =>
  serviciosV2Client.get(`${ART}/${encodeURIComponent(idProducto)}/facturas`, {
    params: cleanParams({ ...baseFiltros(filtros), limit, offset }),
  })

/**
 * Desglose línea-por-línea de TODAS las facturas (auditoría completa).
 * Acepta filtros adicionales: tipo_comprobante, nit_receptor, id_producto,
 * order_by (fecha|importe|cantidad|producto), order_dir (asc|desc).
 */
export const getDesgloseCompleto = ({
  tipo_comprobante, nit_receptor, id_producto,
  order_by, order_dir, limit = 100, offset = 0,
  ...filtros
}) =>
  serviciosV2Client.get(`${ART}/desglose-completo`, {
    params: cleanParams({
      ...baseFiltros(filtros),
      tipo_comprobante, nit_receptor, id_producto,
      order_by, order_dir, limit, offset,
    }),
  })

/**
 * Descarga el desglose completo como CSV o XLSX (streaming del back).
 * Acepta los mismos filtros que getDesgloseCompleto.
 */
export const descargarDesgloseExport = async ({
  tipo_comprobante, nit_receptor, id_producto,
  formato = "xlsx", ...filtros
}) => {
  const params = cleanParams({
    ...baseFiltros(filtros),
    tipo_comprobante, nit_receptor, id_producto,
  })

  let response
  try {
    response = await serviciosV2Client.get(`export/articulos/desglose-completo.${formato}`, {
      params,
      responseType: "blob",
      timeout: 600000,
    })
  } catch (err) {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text()
        const json = JSON.parse(text)
        throw new Error(json.error || json.message || "Error al exportar.")
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message.startsWith("Error al exportar")) throw parseErr
      }
    }
    throw err
  }

  const cd = response.headers["content-disposition"] || ""
  const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
  const filename = match
    ? decodeURIComponent(match[1])
    : `desglose_completo_${filtros.nitemisor}_${new Date().toISOString().split("T")[0]}.${formato}`

  const contentType = response.headers["content-type"] ||
    (formato === "xlsx"
      ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : "text/csv;charset=utf-8;")

  const blob = new Blob([response.data], { type: contentType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  return {
    totalRows: Number(response.headers["x-total-rows"]) || null,
    totalSheets: Number(response.headers["x-total-sheets"]) || null,
    filename,
  }
}

/** Descarga el listado de facturas de un producto como CSV o XLSX (streaming del back). */
export const descargarArticuloFacturasExport = async (idProducto, { formato = "xlsx", ...filtros }) => {
  let response
  try {
    response = await serviciosV2Client.get(`export/articulos/${encodeURIComponent(idProducto)}/facturas.${formato}`, {
      params: baseFiltros(filtros),
      responseType: "blob",
      timeout: 600000,
    })
  } catch (err) {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text()
        const json = JSON.parse(text)
        throw new Error(json.error || json.message || "Error al exportar.")
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message.startsWith("Error al exportar")) throw parseErr
      }
    }
    throw err
  }

  const cd = response.headers["content-disposition"] || ""
  const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
  const filename = match
    ? decodeURIComponent(match[1])
    : `facturas_articulo_${idProducto}_${new Date().toISOString().split("T")[0]}.${formato}`

  const contentType = response.headers["content-type"] ||
    (formato === "xlsx"
      ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : "text/csv;charset=utf-8;")

  const blob = new Blob([response.data], { type: contentType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  return {
    totalRows: Number(response.headers["x-total-rows"]) || null,
    totalSheets: Number(response.headers["x-total-sheets"]) || null,
    filename,
  }
}

/**
 * Descarga CSV o XLSX directo del back (streaming).
 * Respeta Content-Disposition para el filename.
 */
export const descargarExport = async ({ formato = "xlsx", ...filtros }) => {
  let response
  try {
    response = await serviciosV2Client.get(`${EXPORT_BASE}.${formato}`, {
      params: baseFiltros(filtros),
      responseType: "blob",
      timeout: 600000,
    })
  } catch (err) {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text()
        const json = JSON.parse(text)
        throw new Error(json.error || json.message || "Error al exportar.")
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message.startsWith("Error al exportar")) throw parseErr
      }
    }
    throw err
  }

  const cd = response.headers["content-disposition"] || ""
  const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
  const filename = match
    ? decodeURIComponent(match[1])
    : `articulos_${filtros.nitemisor}_${new Date().toISOString().split("T")[0]}.${formato}`

  const contentType = response.headers["content-type"] ||
    (formato === "xlsx"
      ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : "text/csv;charset=utf-8;")

  const blob = new Blob([response.data], { type: contentType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  return {
    totalRows: Number(response.headers["x-total-rows"]) || null,
    totalSheets: Number(response.headers["x-total-sheets"]) || null,
    filename,
  }
}
